import { useTranslations } from 'next-intl'
import {
  Plane,
  ShoppingBag,
  Church,
  Waves,
  Hospital,
  MapPin,
  Train,
  Landmark,
  type LucideIcon,
} from 'lucide-react'

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  airport: Plane,
  mall: ShoppingBag,
  mosque: Church,
  beach: Waves,
  hospital: Hospital,
  transport: Train,
  attraction: Landmark,
}

interface PropertyLandmarksProps {
  landmarks: Array<{
    id: string
    name_en: string
    name_ar: string | null
    category: string
    distance_km: number | null
    distance_text: string | null
  }>
  locale: string
}

export function PropertyLandmarks({ landmarks, locale }: PropertyLandmarksProps) {
  const t = useTranslations('booking.propertyDetail')
  const isRtl = locale === 'ar'

  if (landmarks.length === 0) return null

  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {t('nearbyLandmarks', { defaultMessage: 'What\'s Nearby' })}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
        {landmarks.map((lm) => {
          const Icon = CATEGORY_ICONS[lm.category] || MapPin
          const name = isRtl && lm.name_ar ? lm.name_ar : lm.name_en
          const distance = lm.distance_text || (lm.distance_km ? `${lm.distance_km} km` : null)

          return (
            <div
              key={lm.id}
              className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800 last:border-0"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Icon className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{name}</span>
              </div>
              {distance && (
                <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0 ml-3">
                  {distance}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
